import { BILLING_CLAIMS_STATUSES, CLAIM_STATUS_SELECTOR_OPTIONS } from '../model/statuses';

/**
 * Which claim statuses the edit form must grey out.
 *
 * Five of the eight billing statuses pin the claim status to a single
 * value; only Billed, Ready for Billing and ICD Issue leave a choice.
 * Within that choice the four money statuses are offered solely when
 * the CPT rows justify them — the form never lets someone mark a claim
 * Paid while no amount on it is paid.
 *
 * Returns the DISABLED values, which is what the select expects.
 */

// Offered only when an amount on the CPT rows supports them.
const MONEY_STATUSES = ['paid', 'partiallyPaid', 'patientResponsibility', 'denied'];

const allValues = () => CLAIM_STATUS_SELECTOR_OPTIONS.map(option => option.value);

const allExcept = keep => allValues().filter(value => value !== keep);

// Unary `+`, as the form has always used: a blank or malformed amount
// becomes NaN, and every comparison against it is false. `parseFloat`
// would read "12abc" as 12, which is a different rule.
const amount = raw => +raw;

export const claimStatusDisabledItems = claim => {
  if (!claim) return [...MONEY_STATUSES];

  const { billingStatus, activities } = claim;

  if (
    billingStatus === BILLING_CLAIMS_STATUSES.pending ||
    billingStatus === BILLING_CLAIMS_STATUSES.hold
  ) {
    return allExcept('pending');
  }

  if (
    billingStatus === BILLING_CLAIMS_STATUSES.appointmentCancelled ||
    billingStatus === BILLING_CLAIMS_STATUSES.insuranceIssue ||
    billingStatus === BILLING_CLAIMS_STATUSES.appointmentTypeIssue
  ) {
    return allExcept('notBillable');
  }

  // No CPT rows, no amounts, nothing to justify a money status. The
  // length check is the point: `[].every(...)` is true, so an empty row
  // list used to offer Paid, Patient Responsibility and Denied on a
  // claim that carried no amount anywhere — reachable by deleting every
  // CPT row.
  if (!activities?.length) return [...MONEY_STATUSES];

  const enabled = new Set();
  if (activities.every(row => amount(row.paid) > 0)) enabled.add('paid');
  if (activities.some(row => amount(row.paid) > 0)) enabled.add('partiallyPaid');
  if (activities.every(row => amount(row.pr) > 0)) enabled.add('patientResponsibility');
  if (activities.every(row => !amount(row.pr) && !amount(row.paid))) enabled.add('denied');

  return MONEY_STATUSES.filter(status => !enabled.has(status));
};

export default claimStatusDisabledItems;
